import { Alert, RichTextarea } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { FC, PropsWithChildren, useMemo, type JSX } from 'react';

import {
  PublicEntityTemplate,
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { isNullish } from '@payaca/utilities/guards';
import { useProposalTemplateGrants } from '../createEditProposalTemplateControl/ProposalTemplateGrants';
import PaymentScheduleDefinitionReadView from '../paymentScheduleDefinitionReadView/PaymentScheduleDefinitionReadView';
import './EntityTemplateReadView.sass';

type Props = {
  entityTemplate: PublicEntityTemplate<'proposal'>;
  onUpdateEntityTemplateSuccess?: (
    updatedEntityTemplate: PublicHydratedEntityTemplate<TemplatableEntity>
  ) => void;
};
const EntityTemplateProposalReadView: FC<Props> = ({
  entityTemplate,
  onUpdateEntityTemplateSuccess,
}: Props): JSX.Element | null => {
  if (!entityTemplate) return null;

  const validSummaryMessages = useMemo(() => {
    return (
      entityTemplate.data.summaryMessages?.filter((x) => x.message?.length) ||
      []
    );
  }, [entityTemplate.data.summaryMessages]);

  return (
    <div className="flex flex-col gap-6">
      {!isNullish(entityTemplate?.data?.type) && (
        <div>
          <SectionTitle>Proposal type</SectionTitle>
          {entityTemplate.data.type === 'QUOTE' ? 'Quote' : 'Estimate'}
        </div>
      )}
      {!!entityTemplate?.data?.introduction && (
        <div>
          <SectionTitle>Intro</SectionTitle>
          <SectionDivider />
          <RichTextarea readOnly value={entityTemplate.data.introduction} />
        </div>
      )}
      {!!entityTemplate?.data?.notes && (
        <div>
          <SectionTitle>Notes</SectionTitle>
          <SectionDivider />
          <RichTextarea readOnly value={entityTemplate.data.notes} />
        </div>
      )}
      {!isNullish(entityTemplate?.data?.validForDays) && (
        <div>
          <SectionTitle>Valid for (days)</SectionTitle>
          {entityTemplate.data.validForDays}
        </div>
      )}
      {entityTemplate.data.paymentSchedule && (
        <div>
          <SectionTitle>Payment schedule</SectionTitle>
          <PaymentScheduleDefinitionReadView
            paymentSchedule={entityTemplate.data.paymentSchedule}
          />
        </div>
      )}
      <GrantsReadView grantIds={entityTemplate.data.grantIds ?? []} />
      {!!validSummaryMessages.length && (
        <div>
          <div>
            <SectionTitle>
              Payment summary{' '}
              {validSummaryMessages.length === 1 ? 'message' : 'messages'}
            </SectionTitle>
            <SectionDivider />
            <div className="flex flex-col gap-2">
              {validSummaryMessages.map((message, index) => (
                <Alert
                  iconName={'info-circle.3'}
                  key={index}
                  colour={message.level === 'info' ? 'blue' : 'yellow'}
                >
                  {message.message}
                </Alert>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default EntityTemplateProposalReadView;

const GrantsReadView: FC<{ grantIds: string[] }> = ({ grantIds }) => {
  const { isVisible, selected } = useProposalTemplateGrants(grantIds);

  if (!isVisible || selected.length === 0) return null;

  return (
    <div>
      <SectionTitle>Grants</SectionTitle>
      <SectionDivider />
      <ul className="flex flex-col gap-1">
        {selected.map((grant) => (
          <li key={grant.id}>
            {grant.name} ·{' '}
            <CurrencyValue {...grant.availableAmount} hideZeroMinorUnits />
          </li>
        ))}
      </ul>
    </div>
  );
};

const SectionDivider: FC = () => {
  return <hr className="m-0 my-2" />;
};

const SectionTitle: FC<PropsWithChildren> = ({ children }) => {
  return <h3 className="mb-0 text-sm">{children}</h3>;
};
